/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';

import {
  Box,
  Card,
  Divider,
  Grid,
  Typography,
  FormHelperText,
  IconButton,
  Avatar,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';

import { withStyles } from '@mui/styles';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';

import * as Yup from 'yup';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { PubSub } from '../../../../utils/eventUtils';

import CloseIcon from '../../../../_assets/svg/Close.svg';
// apis
import { updateUserProfileAccount } from '../../../../actions/ProfileActions';
import {
  delProfilePhoto,
  getProfilePhoto,
  postInitialImage,
} from '../../../../api/ImageService';
import { manageAPIError } from '../../../../utils';
import { useActor } from '../../../hooks/useActor';
import { Modal } from '../../../modals/v2/helpers/v2.modal.helpers';
import { UploadProfilePictureConfig } from '../../../modals/UploadProfilePicture';
import { withFormController } from '../../../hocs/forms';
import { useLanguageFormState } from '../../../../i18n/forms/LanguageForm';

import { useMeQuery } from '../../../hooks/useMeQuery';
import useNotifications from '../../../../_lib/notifications/useNotifications';

const styles = ({ common, palette }: any) => {
  return {
    spacing: {
      margin: '1rem 5rem',
    },
    divider: {
      marginBottom: '1.6666666667rem',
    },
    note: {
      fontStyle: 'italic',
      fontSize: common.fontSizes.normal,
      whiteSpace: 'nowrap',
      margin: '0.83rem',
      color: palette.secondary.main,
    },
    headings: {
      marginBottom: 0,
      marginTop: 32,
      fontSize: common.fontSizes.normal,
    },
    avatar: {
      border: `solid 2px ${palette.primary.main}`,
      width: '130px',
      height: '130px',
      background: 'inherit',
      color: 'inherit',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-evenly',
    },
    avatarText: {
      textAlign: 'center',
      fontSize: '10px',
      paddingTop: '0.83rem',
    },
    cloudIcon: {
      width: '3rem',
      height: '3rem',
    },
    logos: {
      width: '130px',
      height: '130px',
    },
    icon: {
      width: '1.66rem',
    },
    iconWrapper: {
      position: 'relative',
      top: '1.91rem',
      right: '5.83rem',
    },
    photoNote: {
      fontStyle: 'italic',
      fontSize: common.fontSizes.petite,
      textAlign: 'left',
      display: 'flex',
      justifyContent: 'center',
      color: palette.primary.main,
    },
    photoWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      alignItems: 'center',
      gap: '8px',
    },
    buttonWrapper: {
      textAlign: 'center',
      paddingLeft: '11.66rem',
    },
    loadingIconRoot: {
      width: '25px !important',
      height: '25px !important',
      position: 'absolute',
      left: 3,
    },
    loadingCloudIconRoot: {
      width: '55px !important',
      height: '55px !important',
      position: 'absolute',
    },
    circles: {
      '&.MuiCircularProgress-circle': { strokeWidth: 1 },
    },
    deleteIconRoot: {
      width: '25px !important',
      height: '25px !important',
      position: 'absolute',
      marginLeft: '-0.1rem',
      marginTop: '3px',
    },
    close: {
      width: '1.5rem',
      paddingTop: 5,
      cursor: 'pointer',
      position: 'relative',
      left: 5,
      fill: palette.primary.main,
    },
  };
};

// @ts-expect-error TS(2345): Argument of type '({ common, palette }: any) => { ... Remove this comment to see the full error message
export const EditUserProfile = withStyles(styles)(({ classes }) => {
  const notifications = useNotifications();
  const languageFormState = useLanguageFormState({
    options: { validatePhone: true, phoneFieldName: 'contactPhone' },
  });

  const dispatch = useDispatch();

  const actor = useActor();
  const queryClient = useQueryClient();

  const [errorMsg, setError] = useState('');
  const [profilePhoto, setPhoto] = useState(false);
  const [photoSrc, setPhotoSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const [delLoading, setDelLoading] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      firstName: actor.firstName || '',
      middleName: actor.middleName || '',
      lastName: actor.lastName || '',
      contactEmail: actor.email || '',
      contactPhone: actor.phone || '',
      id: actor.account.id || '',
      contactReadOnly: actor.mfaType === 'SMS',
    },
    // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
    resolver: yupResolver(deriveSchema(languageFormState.languageFormSchema)),
  });

  formMethods.watch(['contactReadOnly', 'id']);

  const values = formMethods.getValues();

  // `defaultValues` above is only captured on the initial render, so resync
  // once actor data actually becomes available, unless the user has already
  // started editing.
  useEffect(() => {
    if (!actor.id || formMethods.formState.isDirty) {
      return;
    }

    formMethods.reset({
      firstName: actor.firstName || '',
      middleName: actor.middleName || '',
      lastName: actor.lastName || '',
      contactEmail: actor.email || '',
      contactPhone: actor.phone || '',
      id: actor.account.id || '',
      contactReadOnly: actor.mfaType === 'SMS',
    });
    // eslint-disable-next-line
  }, [actor.id]);

  const getAndSetProfilePhoto = () => {
    getProfilePhoto()
      .then((resp) => {
        if (resp.status === 204) {
          setPhoto(false);
        } else if (resp.status === 200) {
          setPhoto(true);
          setPhotoSrc(resp.data);
        }
      })
      .catch((error) => {
        const statusCode = _.get(error, 'response.status');

        if (statusCode === 403) {
          setError('Unable to load your profile picture.');
        } else {
          const errorMessage = manageAPIError(
            error,
            'Something went wrong. Please try again!'
          );

          setError(errorMessage);
        }
      });
  };

  useEffect(() => {
    getAndSetProfilePhoto();

    const sub = PubSub.subscribe('profile:uploaded', (profile: any) => {
      if (profile) {
        getAndSetProfilePhoto();
      }
    });

    formMethods.register('contactReadOnly', { required: false });
    formMethods.register('id', { required: false });

    return () => {
      sub.remove();
      formMethods.unregister('contactReadOnly');
      formMethods.unregister('id');
    };
    // eslint-disable-next-line
  }, []);

  const deleteProfilePhoto = () => {
    setDelLoading(true);
    delProfilePhoto()
      .then(() => {
        setPhoto(false);
        setDelLoading(false);
        notifications.success('Profile Photo Removed!');
        PubSub.publish('profile:deleted', true);
      })
      .catch((error) => {
        setDelLoading(false);
        setError(
          manageAPIError(error, 'Something went wrong. Please try again!')
        );
      });
  };

  const onSubmit = (formValues: any) => {
    const {
      firstName,
      middleName,
      lastName,
      contactEmail,
      contactPhone,
      id,
      ...formRest
    } = formValues;

    return dispatch(
      // @ts-expect-error TS(2345): Argument of type '(dispatch: any) => void' is not ... Remove this comment to see the full error message
      updateUserProfileAccount(
        {
          firstName,
          middleName,
          lastName,
          email: contactEmail,
          phone: contactPhone,
          ...formRest,
        },
        id,
        success,
        error
      )
    );
  };

  const success = (resp: any, account: any) => {
    if (resp.status === 200) {
      queryClient.invalidateQueries([useMeQuery.QUERY_KEY]);

      notifications.success('Profile Info Successfully Updated!');

      formMethods.reset({
        firstName: account.firstName,
        lastName: account.lastName,
        contactEmail: account.email,
        contactPhone: account.phone,
        // @ts-expect-error TS(2345): Argument of type '{ firstName: any; lastName: any;... Remove this comment to see the full error message
        contactPhoneCountryCode: account.contactPhoneCountryCode,
      });
    }
  };

  const error = (resp: any) => {
    setError(
      manageAPIError(
        { response: resp } as any,
        'Something went wrong. Please try again!'
      )
    );
  };

  const uploadImage = useCallback((event: any) => {
    const bodyFormData = new FormData();
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const { size } = file;
    bodyFormData.append('file', file);

    if (size / 1024 / 1024 > 2) {
      setError('The file size is too big, 2MB max.');
    } else {
      setLoading(true);
      postInitialImage(bodyFormData)
        .then((resp) => {
          setLoading(false);
          setError('');
          Modal.show(UploadProfilePictureConfig, { data: resp.data });
        })
        .catch((error) => {
          setLoading(false);
          setError(
            manageAPIError(error, 'Something went wrong, please try again')
          );
        });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Card style={{ height: '100%' }}>
      <Box pl={3} pb={3} pr={3}>
        <Grid item md={10}>
          <Box py={2}>
            <Typography>MY INFO</Typography>
            <Divider />
          </Box>

          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)} noValidate>
              <Grid container spacing={1} className={classes.spacing}>
                <Grid item sm={3} style={{ display: 'flex' }}>
                  <div className={classes.photoWrapper}>
                    <div style={{ display: 'flex' }}>
                      {profilePhoto && (
                        <div onClick={deleteProfilePhoto}>
                          {delLoading && (
                            <CircularProgress
                              classes={{ root: `${classes.deleteIconRoot}` }}
                            />
                          )}
                          <CloseIcon className={classes.close} />
                        </div>
                      )}
                      <Avatar className={classes.avatar}>
                        {profilePhoto ? (
                          <Avatar
                            alt="Profile"
                            src={photoSrc}
                            className={classes.logos}
                          />
                        ) : (
                          <>
                            <div className={classes.avatarText}>
                              Drag and Drop
                              <br /> an image file here or Click
                            </div>
                            <div>
                              <input
                                accept=".png,.jpg,.jpeg,.gif"
                                style={{ display: 'none' }}
                                id="icon-button-file"
                                type="file"
                                onChange={uploadImage}
                              />
                              <label htmlFor="icon-button-file">
                                <IconButton
                                  color="primary"
                                  aria-label="upload picture"
                                  component="span"
                                  style={{ padding: 5 }}
                                  size="large"
                                >
                                  {loading && (
                                    <CircularProgress
                                      classes={{
                                        root: `${classes.loadingCloudIconRoot}`,
                                        circle: `${classes.circles}`,
                                      }}
                                    />
                                  )}
                                  <CloudUploadOutlinedIcon
                                    className={classes.cloudIcon}
                                  />
                                </IconButton>
                              </label>
                            </div>
                          </>
                        )}
                      </Avatar>
                      <div>
                        {profilePhoto && (
                          <>
                            <input
                              accept=".png,.jpg,.jpeg,.gif"
                              style={{ display: 'none' }}
                              id="icon-button-file1"
                              type="file"
                              onChange={uploadImage}
                            />
                            <label htmlFor="icon-button-file1">
                              <IconButton
                                color="primary"
                                aria-label="upload picture"
                                component="span"
                                style={{ padding: 5, right: '1rem' }}
                                size="large"
                              >
                                {loading && (
                                  <CircularProgress
                                    classes={{
                                      root: `${classes.loadingIconRoot}`,
                                    }}
                                  />
                                )}
                                <EditIcon className={classes.icon} />
                              </IconButton>
                            </label>
                          </>
                        )}
                      </div>
                    </div>

                    <div className={classes.photoNote}>
                      Please select a PNG, JPG or GIF image file <br />
                      with size smaller than 2MB to upload.
                    </div>
                  </div>
                </Grid>

                <Grid item md={7}>
                  <Grid container direction="row" spacing={2}>
                    <Grid item md={4}>
                      <ControlledTextField
                        name="firstName"
                        label="First Name"
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item md={4}>
                      <ControlledTextField
                        name="middleName"
                        label="Middle Name"
                        fullWidth
                      />
                    </Grid>
                    <Grid item md={4}>
                      <ControlledTextField
                        name="lastName"
                        label="Last Name"
                        required
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={2}>
                    <Grid item md={6}>
                      <ControlledTextField
                        name="contactEmail"
                        label="Contact Email"
                        required
                        disabled
                        fullWidth
                      />
                    </Grid>
                    <Grid item md={6}>
                      <ControlledMuiTelInput
                        name="contactPhone"
                        label="Contact Phone"
                        forceCallingCode
                        onlyCountries={['US', 'GB']}
                        defaultCountry="US"
                        fullWidth
                        disabled={values.contactReadOnly}
                        helperText={
                          values.contactReadOnly &&
                          'Disable SMS Authentication to make changes to Contact Phone!'
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {errorMsg && (
                <FormHelperText
                  className="api-text-error"
                  style={{ textAlign: 'left' }}
                >
                  {errorMsg}
                </FormHelperText>
              )}

              <Divider className={classes.divider} />
              <Box className={classes.buttonWrapper}>
                <Button
                  size="large"
                  variant="contained"
                  type="submit"
                  disabled={
                    formMethods.formState.isSubmitting ||
                    !formMethods.formState.isDirty
                  }
                >
                  Save Changes
                </Button>
              </Box>
            </form>
          </FormProvider>
        </Grid>
      </Box>
    </Card>
  );
});

const ControlledTextField = withFormController(TextField);

const ControlledMuiTelInput = withFormController(MuiTelInput);

const deriveSchema = () =>
  Yup.object().shape({
    firstName: Yup.string()
      .label('First Name')
      .matches(/^[aA-zZ\s]+$/, 'Only alphabets are allowed for this field')
      .required(),
    lastName: Yup.string()
      .label('Last Name')
      .matches(/^[aA-zZ\s]+$/, 'Only alphabets are allowed for this field')
      .required(),
    contactEmail: Yup.string().label('Contact Email').email().required(),
    contactPhone: Yup.string()
      .label('Phone')
      .test('validPhone', 'Contact Phone must be valid', (val) => {
        return matchIsValidTel(val);
      }),
  });
